// ============================================================
// ClientTasksTab.tsx
// The client page's TASKS tab — approved mockup Option A
// (casevo-client-tasks-tab.html / CA1122_1.HTM): the client's tasks as the
// EXACT My Work rows (QueueRow, MyWork.tsx) grouped by urgency, a status
// filter with counts (Cases-tab segmented control), a Sort select, the
// dashed "Add a task for <client>" row (the Cases add-tile as a row), the
// friendly empty state, a skeleton, an error banner with retry, and a task
// actions menu (⋮ + right-click) for Cases-tab parity.
//
// Behaviour is My Work's, not a copy of it:
//   • rows      — QueueRow (tick, title + In-progress, case link + ref, two-line
//                 due, people chips → quick reassign, priority pill → quick
//                 reprioritise, star)
//   • actions   — quickUpdateTask (taskActions.ts, the engine TasksView uses):
//                 optimistic, serialised, server-revert; patches BOTH this list
//                 and store.tasks (My Work) — a task ticked here moves there.
//   • perms     — taskPermsFor (the same gates TasksView applies)
//   • delete    — store.deleteTask (deleteTaskApi + store list + favourites),
//                 owner/admin only, behind a confirm
//   • toast     — MyWorkParts Toast with Undo, same copy as My Work
// ============================================================
import { refusalText } from '../../utils/serverError'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import type { Task, TaskPriority, TaskStatus, UpdateTaskPayload } from '../../types/tasks.types'
import { useCasevoStore } from '../../store/useCasevoStore'
import { QueueRow, type CaseInfo } from '../tasks/MyWork'
import { CARD, HAIR, POP, POP_HEAD, POP_OPT, RED, Ic, PriorityPill, GroupHeading, Toast, useDismiss } from '../tasks/MyWorkParts'
import { urgencyOf, compareQueue, PRIORITY_RANK, type Urgency } from '../tasks/myWorkLogic'
import { quickUpdateTask, taskPermsFor } from '../tasks/taskActions'
import { normalisePriority, PRIORITY_LABELS } from '../shared/TaskRow'
import ConfirmDialogue from '../shared/ConfirmDialogue'
import ActionBox from '../shared/ActionBox'

type Filter = 'all' | TaskStatus
type SortKey = 'due' | 'priority' | 'case'

const GROUP_LABEL: Record<Urgency, string> = {
  overdue: 'Overdue', today: 'Today', week: 'This week', later: 'Later', none: 'No due date', done: 'Done',
}
const URGENCY_GROUPS: Urgency[] = ['overdue', 'today', 'week', 'later', 'none', 'done']

export interface ClientTasksTabProps {
  clientFirstName: string
  /** null = loading. */
  tasks: Task[] | null
  error: string | null
  onRetry: () => void
  caseInfo: (caseId: string | null) => CaseInfo | undefined
  onOpenTask: (id: string) => void
  onNewTask: () => void
  onOpenCase: (caseId: string) => void
  /** Patch / remove a row in the page's local client-task list. */
  onPatch: (id: string, fn: (t: Task) => Task) => void
  onRemoved: (id: string) => void
}

// ── Add-task row (.addrow — the Cases add-tile, as a row) ─────────────
const AddTaskRow: React.FC<{ first: string; onClick: () => void }> = ({ first, onClick }) => (
  <button type="button" onClick={onClick}
    className={'group flex items-center gap-[12px] w-full mt-[12px] px-[14px] py-[12px] rounded-[14px] border-[1.5px] border-dashed border-[color-mix(in_srgb,var(--casevo-mid)_55%,var(--casevo-border))] bg-transparent ' +
      'text-casevo-brand dark:text-[#a8e6c5] text-[14px] font-semibold text-left transition-[background-color,border-color,transform] duration-150 ' +
      'hover:bg-[color-mix(in_srgb,var(--casevo-mint)_45%,transparent)] hover:border-casevo-mid hover:-translate-y-px motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus:outline-none casevo-focus'}>
    <span className="w-[28px] h-[28px] rounded-[9px] bg-casevo-mint grid place-items-center transition-transform duration-150 group-hover:rotate-90 motion-reduce:transition-none">
      <Ic n="plus" className="w-[15px] h-[15px]" />
    </span>
    Add a task for {first}
    <small className="ml-auto font-normal text-[12.5px] text-casevo-text-soft">Opens the task form with {first} pre-selected</small>
  </button>
)

// ── Task actions menu (⋮ / right-click) — .ctxm ──────────────────────
interface MenuState { task: Task; x: number; y: number }
const MI = `${POP_OPT}`
const TaskMenu: React.FC<{
  m: MenuState; canStatus: boolean; canEdit: boolean; canDelete: boolean; fav: boolean
  onClose: () => void; onOpen: () => void; onToggleDone: () => void; onProgress: () => void
  onPriority: (p: TaskPriority) => void; onOpenCase?: () => void; onFav: () => void; onDelete: () => void
}> = ({ m, canStatus, canEdit, canDelete, fav, onClose, onOpen, onToggleDone, onProgress, onPriority, onOpenCase, onFav, onDelete }) => {
  const ref = useRef<HTMLDivElement>(null)
  useDismiss(ref, onClose)
  const t = m.task
  const done = t.status === 'done'
  const pr = normalisePriority(t.priority)
  // Keep the 220px menu on-screen.
  const left = Math.min(m.x, window.innerWidth - 232)
  const top = Math.min(m.y, window.innerHeight - 360)
  const act = (fn: () => void) => (): void => { onClose(); fn() }
  return (
    <div ref={ref} role="menu" aria-label={`Actions for ${t.title}`} style={{ left, top }}
      className={`${POP} !fixed z-[60] w-[220px]`} onContextMenu={(e) => e.preventDefault()}>
      <button type="button" role="menuitem" className={MI} onClick={act(onOpen)}><Ic n="arrow" />Open task</button>
      {canStatus && (
        <button type="button" role="menuitem" className={MI} onClick={act(onToggleDone)}>
          <Ic n={done ? 'refresh' : 'check'} />{done ? 'Reopen' : 'Mark done'}
        </button>
      )}
      {canStatus && !done && (
        <button type="button" role="menuitem" className={MI} onClick={act(onProgress)}>
          <Ic n="clock" />{t.status === 'in_progress' ? 'Back to open' : 'Mark in progress'}
        </button>
      )}
      {canEdit && !done && (
        <>
          <hr className={`border-0 border-t ${HAIR} mx-[2px] my-[4px]`} />
          <div className={POP_HEAD}>Priority</div>
          <div className="flex items-center gap-[4px] px-[8px] pb-[6px]">
            {(['high', 'normal', 'low'] as TaskPriority[]).map((p) => (
              <button key={p} type="button" role="menuitemradio" aria-checked={pr === p} onClick={act(() => { if (p !== pr) onPriority(p) })}
                className={`rounded-full focus:outline-none casevo-focus ${pr === p ? 'ring-2 ring-casevo-mid ring-offset-1 ring-offset-casevo-surface' : 'opacity-80 hover:opacity-100'}`}>
                <PriorityPill p={p} />
              </button>
            ))}
          </div>
        </>
      )}
      <hr className={`border-0 border-t ${HAIR} mx-[2px] my-[4px]`} />
      {onOpenCase && <button type="button" role="menuitem" className={MI} onClick={act(onOpenCase)}><Ic n="folder" />Open case</button>}
      <button type="button" role="menuitem" className={MI} onClick={act(onFav)}>
        <svg viewBox="0 0 24 24" className={`w-[14px] h-[14px] shrink-0 ${fav ? 'text-casevo-gold' : ''}`} fill={fav ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinejoin="round" aria-hidden="true">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
        {fav ? 'Remove from favourites' : 'Add to favourites'}
      </button>
      {canDelete && (
        <>
          <hr className={`border-0 border-t ${HAIR} mx-[2px] my-[4px]`} />
          <button type="button" role="menuitem" className={`${MI} !text-[#c23b2c] dark:!text-[#f3a094]`} onClick={act(onDelete)}><Ic n="x" />Delete task</button>
        </>
      )}
    </div>
  )
}

const ClientTasksTab: React.FC<ClientTasksTabProps> = (p) => {
  const first = p.clientFirstName.trim() || 'this client'
  const authUser = useCasevoStore((s) => s.authUser)
  const firmUsers = useCasevoStore((s) => s.firmUsers)
  const favourites = useCasevoStore((s) => s.favourites)
  const perms = useMemo(() => taskPermsFor(authUser), [authUser])
  const byId = useMemo(() => new Map(firmUsers.map((u) => [u.id, u])), [firmUsers])
  const now = useMemo(() => new Date(), [p.tasks]) // eslint-disable-line react-hooks/exhaustive-deps

  const [filter, setFilter] = useState<Filter>('all')
  const [sort, setSort] = useState<SortKey>('due')
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [confirmDel, setConfirmDel] = useState<Task | null>(null)

  // ── toast (MyWork.tsx flash/run, same copy) ──
  const [toast, setToast] = useState<null | { text: React.ReactNode; undo?: () => void; error?: boolean }>(null)
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const flash = useCallback((t: { text: React.ReactNode; undo?: () => void; error?: boolean }) => {
    if (toastTimer.current) clearTimeout(toastTimer.current)
    setToast(t)
    toastTimer.current = setTimeout(() => setToast(null), t.undo ? 6000 : 4000)
  }, [])
  const { onPatch } = p
  const update = useCallback((id: string, payload: UpdateTaskPayload) => quickUpdateTask(id, payload, onPatch), [onPatch])
  const run = useCallback(async (id: string, payload: UpdateTaskPayload, ok?: { text: React.ReactNode; undo?: UpdateTaskPayload }) => {
    if (ok) flash({ text: ok.text, undo: ok.undo ? () => { void update(id, ok.undo as UpdateTaskPayload).catch((err) => flash({ text: refusalText(err, 'Couldn’t undo that'), error: true })); setToast(null) } : undefined })
    try {
      await update(id, payload)
    } catch (err) {
      const serverMsg = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error
      flash({ text: typeof serverMsg === 'string' && serverMsg ? serverMsg : err instanceof Error ? err.message : 'Couldn’t save that change', error: true })
    }
  }, [flash, update])
  const toggleDone = (t: Task): void => {
    const next: TaskStatus = t.status === 'done' ? 'open' : 'done'
    void run(t.id, { status: next }, next === 'done'
      ? { text: <>Marked done — <b className="font-semibold">{t.title}</b></>, undo: { status: t.status } }
      : { text: <>Reopened — <b className="font-semibold">{t.title}</b></> })
  }
  const setPriority = (t: Task, pr: TaskPriority): void => {
    void run(t.id, { priority: pr }, { text: <>Priority set to <b className="font-semibold">{PRIORITY_LABELS[pr]}</b></>, undo: { priority: normalisePriority(t.priority) } })
  }
  const toggleProgress = (t: Task): void => {
    const next: TaskStatus = t.status === 'in_progress' ? 'open' : 'in_progress'
    void run(t.id, { status: next }, { text: <>{next === 'in_progress' ? 'In progress' : 'Back to open'} — <b className="font-semibold">{t.title}</b></>, undo: { status: t.status } })
  }
  const doDelete = async (t: Task): Promise<void> => {
    setConfirmDel(null)
    try {
      await useCasevoStore.getState().deleteTask(t.id)
      p.onRemoved(t.id)
      flash({ text: <>Deleted — <b className="font-semibold">{t.title}</b></> })
    } catch (err) {
      flash({ text: err instanceof Error ? err.message : 'Couldn’t delete that task', error: true })
    }
  }

  // ── counts + filtering ──
  const all = useMemo(() => p.tasks ?? [], [p.tasks])
  const counts = useMemo(() => {
    const o = { all: all.length, open: 0, in_progress: 0, done: 0, overdue: 0 }
    for (const t of all) {
      o[t.status] += 1
      if (urgencyOf(t, now) === 'overdue') o.overdue += 1
    }
    return o
  }, [all, now])
  const shown = useMemo(() => all.filter((t) => filter === 'all' || t.status === filter), [all, filter])

  const groups = useMemo((): { key: string; label: string; tone: 'overdue' | 'today' | 'plain'; items: Task[] }[] => {
    if (sort === 'due') {
      return URGENCY_GROUPS.map((u) => ({
        key: u, label: GROUP_LABEL[u], tone: u === 'overdue' ? 'overdue' as const : u === 'today' ? 'today' as const : 'plain' as const,
        items: shown.filter((t) => urgencyOf(t, now) === u).sort((a, b) => compareQueue(a, b, now)),
      })).filter((g) => g.items.length > 0)
    }
    const map = new Map<string, { label: string; rank: string; items: Task[] }>()
    for (const t of shown) {
      let k: string, label: string, rank: string
      if (sort === 'priority') {
        const pr = normalisePriority(t.priority)
        k = pr; label = `${PRIORITY_LABELS[pr]} priority`; rank = String(PRIORITY_RANK[pr])
      } else {
        const info = p.caseInfo(t.caseId)
        k = t.caseId ?? '~none'; label = t.caseId ? `${info?.name ?? 'Case'}${info?.ref ? ` · ${info.ref}` : ''}` : 'Client tasks — no case'; rank = t.caseId ? label : '~'
      }
      const g = map.get(k) ?? { label, rank, items: [] }
      g.items.push(t)
      map.set(k, g)
    }
    return Array.from(map.entries()).sort((a, b) => (a[1].rank < b[1].rank ? -1 : 1))
      .map(([key, g]) => ({ key, label: g.label, tone: 'plain' as const, items: g.items.sort((a, b) => compareQueue(a, b, now)) }))
  }, [sort, shown, now, p])

  const isFav = (id: string): boolean => (favourites ?? []).some((f) => f.itemType === 'task' && f.itemId === id)

  const rowFor = (t: Task): React.ReactNode => {
    const canEdit = perms.canEditFully(t)
    return (
      <QueueRow key={t.id} task={t} now={now} info={p.caseInfo(t.caseId)} byId={byId}
        currentUserId={perms.currentUserId} highlighted={false} canEdit={canEdit}
        canReassign={(perms.canAssignPeople || perms.canAssignFirm) && (canEdit || t.createdByUserId === perms.currentUserId)}
        canDismiss={false} canClaim={false} canStatus={perms.canStatus(t)} isAdmin={perms.isAdmin} canSeeFirm={perms.isAdmin || (authUser?.effectivePermissions?.seeFirmWideTasks ?? false)}
        firmUsers={firmUsers} canPeople={perms.canAssignPeople} canFirm={perms.canAssignFirm}
        onOpen={() => p.onOpenTask(t.id)} onToggleDone={() => toggleDone(t)}
        onPriority={(pr) => setPriority(t, pr)} onAudience={(payload) => void run(t.id, payload)}
        onClaim={() => {}} onDismiss={() => {}} onOpenCase={p.onOpenCase} onOpenClient={() => {}}
        noCaseLabel="Client task — no case" onMenu={(x, y) => setMenu({ task: t, x, y })} />
    )
  }

  // ── header (.shead) ──
  const subtitle = p.error
    ? `${first}’s tasks`
    : p.tasks === null
    ? 'Loading tasks…'
    : all.length === 0
      ? `${first} has no tasks yet`
      : null
  const header = (
    <div className="flex items-end gap-3 mb-[14px]">
      <div className="flex-1 min-w-0">
        <h2 className="m-0 text-[20px] font-semibold tracking-[-0.01em] text-casevo-forest">Tasks</h2>
        <div className="text-[13px] text-casevo-text-soft mt-0.5">
          {subtitle ?? (
            <>
              {first}’s tasks
              {counts.overdue > 0 && <> · <b className={`font-semibold ${RED}`}>{counts.overdue} overdue</b></>}
              {' · '}{counts.open + counts.in_progress} open · {counts.done} done
            </>
          )}
        </div>
      </div>
    </div>
  )

  const SEGS: { key: Filter; label: string; n: number }[] = [
    { key: 'all', label: 'All', n: counts.all },
    { key: 'open', label: 'Open', n: counts.open },
    { key: 'in_progress', label: 'In progress', n: counts.in_progress },
    { key: 'done', label: 'Done', n: counts.done },
  ]
  const toolbar = (
    <div className="flex items-center gap-[10px] mb-4 flex-wrap">
      <div role="group" aria-label="Filter tasks by status" className="inline-flex border border-casevo-border rounded-[10px] overflow-hidden bg-casevo-surface">
        {SEGS.map((s) => {
          const on = filter === s.key
          return (
            <button key={s.key} type="button" aria-pressed={on} onClick={() => setFilter(s.key)}
              className={`inline-flex items-center gap-1.5 h-8 px-3 text-[13px] border-r border-casevo-border last:border-r-0 focus:outline-none casevo-focus ${on ? 'bg-casevo-primary text-white font-semibold' : 'text-casevo-mid hover:bg-casevo-mint/40'}`}>
              {s.label} <small className="text-[11.5px] opacity-80">{s.n}</small>
            </button>
          )
        })}
      </div>
      <label className="relative inline-flex items-center gap-[7px] h-8 px-[11px] border border-casevo-border rounded-[9px] bg-casevo-surface text-[13px] text-casevo-forest cursor-pointer focus-within:ring-2 focus-within:ring-casevo-mid">
        <span className="text-casevo-text-soft">Sort</span>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)} aria-label="Sort tasks"
          className="appearance-none bg-transparent border-0 p-0 pr-[19px] text-[13px] leading-[1.5] text-casevo-forest focus:outline-none cursor-pointer">
          <option value="due">Due date</option>
          <option value="priority">Priority</option>
          <option value="case">Case</option>
        </select>
        <Ic n="chev" className="w-[12px] h-[12px] text-casevo-text-soft absolute right-[11px] pointer-events-none" />
      </label>
      <span className="ml-auto text-[12.5px] text-casevo-text-soft hidden sm:inline-flex items-center gap-1.5"><Ic n="check" className="w-[12px] h-[12px]" />Tick to complete · right-click for more</span>
    </div>
  )

  const overlays = (
    <>
      {menu && (
        <TaskMenu m={menu}
          canStatus={perms.canStatus(menu.task)} canEdit={perms.canEditFully(menu.task)} canDelete={perms.canDelete(menu.task)}
          fav={isFav(menu.task.id)} onClose={() => setMenu(null)}
          onOpen={() => p.onOpenTask(menu.task.id)} onToggleDone={() => toggleDone(menu.task)} onProgress={() => toggleProgress(menu.task)}
          onPriority={(pr) => setPriority(menu.task, pr)}
          onOpenCase={menu.task.caseId ? () => p.onOpenCase(menu.task.caseId as string) : undefined}
          onFav={() => { void useCasevoStore.getState().toggleFavourite({ itemType: 'task', itemId: menu.task.id, label: menu.task.title }) }}
          onDelete={() => setConfirmDel(menu.task)} />
      )}
      <ConfirmDialogue isOpen={confirmDel !== null} title="Delete task?"
        message={confirmDel ? `“${confirmDel.title}” will be permanently deleted. This can’t be undone.` : ''}
        confirmLabel="Delete" onConfirm={() => { if (confirmDel) void doDelete(confirmDel) }} onCancel={() => setConfirmDel(null)} />
      {toast && <Toast text={toast.text} error={toast.error} action={toast.undo ? { label: 'Undo', onClick: toast.undo } : undefined} />}
    </>
  )

  // ── error ──
  if (p.error) {
    return (
      <div className="text-[14px] leading-[1.5] text-casevo-forest">
        {header}
        <ActionBox alert tone="danger" className="mb-[14px]" message={<>Couldn’t load {first}’s tasks.</>}
          actions={[{ label: 'Try again', kind: 'link', onClick: p.onRetry }]} />
      </div>
    )
  }

  // ── loading (skeleton) ──
  if (p.tasks === null) {
    return (
      <div className="text-[14px] leading-[1.5] text-casevo-forest">
        {header}
        <div className={`${CARD} overflow-hidden`} aria-busy="true" aria-label="Loading tasks">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className={`h-[62px] flex items-center gap-[12px] px-[14px] border-t first:border-t-0 ${HAIR} animate-pulse motion-reduce:animate-none`}>
              <i className="block w-[22px] h-[22px] rounded-full bg-casevo-border-soft" />
              <i className="block flex-1 max-w-[340px] h-[12px] rounded-[6px] bg-casevo-border-soft" />
              <i className="block w-[90px] h-[12px] rounded-[6px] bg-casevo-border-soft ml-auto" />
              <i className="block w-[60px] h-[22px] rounded-full bg-casevo-border-soft" />
            </div>
          ))}
        </div>
      </div>
    )
  }

  // ── empty ──
  if (all.length === 0) {
    return (
      <div className="text-[14px] leading-[1.5] text-casevo-forest">
        {header}
        <div className="py-10 px-6 text-center rounded-[14px] border-[1.5px] border-dashed border-[color-mix(in_srgb,var(--casevo-mid)_45%,var(--casevo-border))] bg-casevo-surface">
          <div aria-hidden="true" className="w-[64px] mx-auto mb-[14px] flex flex-col gap-[6px]">
            <div className="flex items-center gap-[7px]"><span className="w-[14px] h-[14px] rounded-full border-[1.8px] bg-casevo-primary border-casevo-primary" /><i className="block h-[6px] rounded-[3px] bg-casevo-border-soft flex-1" /></div>
            <div className="flex items-center gap-[7px]"><span className="w-[14px] h-[14px] rounded-full border-[1.8px] border-casevo-border" /><i className="block h-[6px] rounded-[3px] bg-casevo-border-soft w-[70%]" /></div>
            <div className="flex items-center gap-[7px]"><span className="w-[14px] h-[14px] rounded-full border-[1.8px] border-casevo-border" /><i className="block h-[6px] rounded-[3px] bg-casevo-border-soft w-[50%]" /></div>
          </div>
          <h3 className="m-0 mb-1 text-[17px] font-bold">No tasks yet</h3>
          <p className="mx-auto mt-0 mb-[18px] max-w-[400px] text-[13.5px] text-casevo-text-soft">
            Add {first}’s first task — link it to one of their cases or keep it on the client.
          </p>
          <button type="button" onClick={p.onNewTask}
            className="inline-flex items-center justify-center gap-2 h-10 pl-[14px] pr-[18px] rounded-[11px] bg-casevo-primary text-white text-[14px] font-semibold border-0 shadow-[0_6px_16px_rgba(45,106,79,0.28)] dark:shadow-[0_6px_16px_rgba(0,0,0,0.35)] transition-colors hover:bg-casevo-primary-hover focus:outline-none casevo-focus">
            <Ic n="plus" className="w-[16px] h-[16px]" />Create the first task
          </button>
        </div>
        {overlays}
      </div>
    )
  }

  const statusWord = filter === 'open' ? 'open' : filter === 'in_progress' ? 'in-progress' : 'done'
  return (
    <div className="text-[14px] leading-[1.5] text-casevo-forest">
      {header}
      {toolbar}
      {groups.length === 0 ? (
        <div className={`${CARD} px-[16px] py-[18px] text-[13px] text-casevo-text-soft`}>
          No {statusWord} tasks — <button type="button" onClick={() => setFilter('all')} className="text-casevo-brand underline underline-offset-2 focus:outline-none casevo-focus">show all</button>
        </div>
      ) : groups.map((g) => (
        <div key={g.key} className="mt-[20px]">
          <GroupHeading label={g.label} count={g.items.length} tone={g.tone} />
          <div className={CARD}>{g.items.map(rowFor)}</div>
        </div>
      ))}
      {filter !== 'done' && <AddTaskRow first={first} onClick={p.onNewTask} />}
      {overlays}

    </div>
  )
}

export default ClientTasksTab
